export const aiopsSessionItems = [
	{
		type: "SET",
		stimulus: {
			id: 50100,
			content: {
				instruction:
					"Watch the video and select the correct answer from the options provided.",
				passage: {
					title: "AI Item Generation Incident – Elevate Learning",
					paragraphs: [
						{
							text: "Elevate Learning's AI-powered item generation system, ItemForge, was used to produce a batch of 40 Grade 8 life science assessment items. After a standard review, the batch was published on Friday afternoon. By Monday morning, teachers from three school districts had filed complaints about factual errors, a fabricated academic citation, and an ambiguous item with two valid answers. Maya Osei, VP of Product, has recorded the following video message for the AI Operations team:",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "video1",
						url: "vi2oyGWjSM7VyJ81UQW00tu1",
						type: "video",
						transcript: `Team, it's Maya. Urgent update on ItemForge. We have a major problem with the Grade 8 science batch that went live Friday. We’ve received dozens of tickets and district-level complaints already. Item 17 has a wrong key—meiosis versus mitosis. Item 31 includes a hallucinated citation to a non-existent study. And Item 9 is flagging scientifically valid answers as wrong. This has hit twelve thousand students.

I also need a draft explanation for our school partners that is honest and clear about these AI hallucinations. We need to be transparent, not defensive, with our teachers and administrators. Production is halted immediately. I need a root cause audit and a plan for mandatory human guardrails by EOD. The logs are in the shared drive. Let’s get on this.`,
					},
				],
			},
			metadata: {
				type: "READING",
				skill: "READING",
				title: "Reading Stimulus",
			},
		},
		items: [
			{
				id: 50101,
				type: "MCSS",
				content: {
					parentId: 50100,
					parentType: "SET",
					stem: "After watching Maya's video regarding the ItemForge incident, what is the most critical immediate action you should take?",
					choices: [
						{
							id: "f7c3a1e9-2b4d-4f6e-8a0c-1d3e5f7a9b2c",
							stem: "Pull the prompt log and ItemForge's raw output from the shared drive before taking any other action",
							text: "Pull the prompt log and ItemForge's raw output from the shared drive before taking any other action",
							isCorrect: true,
							explanation: `
An AI-generated content incident of this scale demands **systematic investigation before any corrective action is taken**. Without establishing the root cause, fixes applied to the system will be arbitrary and may fail to prevent recurrence — or worse, address the wrong problem entirely.

**Pulling the prompt log and ItemForge's raw output** is the most critical first step because it forms the **evidentiary foundation** for the entire incident response. The prompt reveals whether the failure originated in the instruction design — for example, no guidance on source citation, no instruction to flag uncertainty, no constraint on fabrication — while the raw output confirms whether errors were consistent across the batch or isolated to specific item types. This data directly informs every subsequent decision: what to communicate to schools, what to correct in the deployed content, and what guardrails or evaluators to implement.

**Immediately disabling ItemForge** is a reflexive action that disrupts operations without informing the fix. The root cause remains unknown, and the system will be restored in the same broken state.

**Contacting school districts before the investigation** risks communicating inaccurate or incomplete information — compounding the trust damage already done by introducing a second wave of corrections or retractions.

**Reassigning all items for manual rewriting** addresses three visible symptoms but ignores the possibility that other items in the batch contain undetected errors. Without the audit logs, the full scope of the incident is unknown.

**Key Takeaways:**
- **Audit-first response** is the correct protocol for AI system failures — investigation must precede communication and remediation.
- **Prompt logs and model outputs** are the primary artifacts for diagnosing hallucination, factual error, and construct validity failures in AI-generated content.
- **Root cause identification** determines whether the failure is a model limitation, a prompt design gap, or a review process failure — each requiring a different fix.
- **Premature external communication** during an AI incident can escalate stakeholder concerns if the full scope is not yet understood.
- **Systematic investigation** protects both the organization and affected users by ensuring that the response is accurate, comprehensive, and calibrated to what actually went wrong.

**References:**
- Weidinger, L., et al. (2022). *Taxonomy of risks posed by language models*. Proceedings of the ACM Conference on Fairness, Accountability, and Transparency. https://doi.org/10.1145/3531146.3533088
- Bommasani, R., et al. (2021). *On the opportunities and risks of foundation models*. Stanford Center for Research on Foundation Models. https://arxiv.org/abs/2108.07258
- NIST. (2023). *Artificial intelligence risk management framework (AI RMF 1.0)*. National Institute of Standards and Technology. https://doi.org/10.6028/NIST.AI.100-1`,
						},
						{
							id: "a2b4c6d8-e0f2-4a4b-9c6d-8e0f2a4b6c8d",
							stem: "Immediately disable ItemForge across all modules to prevent further item generation",
							text: "Immediately disable ItemForge across all modules to prevent further item generation",
							isCorrect: false,
						},
						{
							id: "b3c5d7e9-f1a3-4b5c-8d7e-9f1a3b5c7d9e",
							stem: "Contact the affected school districts immediately to issue a public apology",
							text: "Contact the affected school districts immediately to issue a public apology",
							isCorrect: false,
						},
						{
							id: "c4d6e8f0-a2b4-4c6d-9e8f-0a2b4c6d8e0f",
							stem: "Reassign all forty flagged items to curriculum staff for manual rewriting",
							text: "Reassign all forty flagged items to curriculum staff for manual rewriting",
							isCorrect: false,
						},
					],
					instruction: "Choose the best answer",
					highlights: null,
					media: null,
				},
				tags: [
					{
						id: 6490,
						type: "Skill",
						label: "Incident Response",
						breadcrumb: "Incident Response",
					},
				],
				metadata: {
					type: "MCSS",
					skill: "READING",
					title: "Multiple Choice Single Selection",
					topics: ["AI Incident Response"],
				},
				response: {
					maximumScore: 1,
					correctResponse: [{ id: "f7c3a1e9-2b4d-4f6e-8a0c-1d3e5f7a9b2c" }],
					numChoicesRequired: { max: 1, min: 1 },
				},
			},
		],
	},
	// Set 2 – Writing (Email to School Partners)
	{
		type: "SET",
		stimulus: {
			id: 50110,
			content: {
				instruction: "Watch the video and then respond as instructed.",
				passage: {
					title: "ItemForge Incident – Draft Your Stakeholder Email",
					paragraphs: [
						{
							text: "Elevate Learning's AI item generation system, ItemForge, produced a batch of Grade 8 life science items containing a wrong answer key, a fabricated academic citation, and an ambiguous item with two valid answers. The batch was served to over 12,000 students before the errors were detected. Maya Osei, VP of Product, has asked you to draft a communication to school partners explaining what happened. The video below provides the full context:",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "video2",
						url: "vi2oyGWjSM7VyJ81UQW00tu1",
						type: "video",
						transcript: `Team, it's Maya. Urgent update on ItemForge. We have a major problem with the Grade 8 science batch that went live Friday. We’ve received dozens of tickets and district-level complaints already. Item 17 has a wrong key—meiosis versus mitosis. Item 31 includes a hallucinated citation to a non-existent study. And Item 9 is flagging scientifically valid answers as wrong. This has hit twelve thousand students.

I also need a draft explanation for our school partners that is honest and clear about these AI hallucinations. We need to be transparent, not defensive, with our teachers and administrators. Production is halted immediately. I need a root cause audit and a plan for mandatory human guardrails by EOD. The logs are in the shared drive. Let’s get on this.`,
					},
				],
			},
			metadata: {
				type: "INTEGRATED_WRITING_STIMULUS",
				skill: "WRITING",
				title: "Role-Play Writing Stimulus",
				topics: ["Stakeholder Communication"],
			},
		},
		items: [
			{
				id: 50111,
				versionId: 1,
				content: {
					parentId: 50110,
					parentType: "SET",
					stem: "Based on Maya's video, draft an email to Elevate Learning's school partners that:\n- acknowledges the errors in the Grade 8 life science item batch\n- explains what AI hallucination is in plain language without technical jargon\n- corrects any misconceptions the audience may have about AI reliability\n- outlines the immediate steps Elevate is taking to address the issue\nYour tone should be honest, professional, and non-defensive.",
					choices: [],
					instruction: "Write your response in the text box provided.",
					media: null,
				},
				tags: [
					{
						id: 6491,
						type: "Skill",
						label: "Writing",
						breadcrumb: "Writing",
					},
				],
				response: {
					maximumScore: 15,
					subskillsSchema: [
						{
							name: "Clarity and Organization",
							icon: "file-document-outline",
							description: "Structure your response clearly.",
							maxScore: 5,
						},
						{
							name: "Crisis Management",
							icon: "shield-check-outline",
							description: "Effectively address the incident.",
							maxScore: 5,
						},
						{
							name: "Justification",
							icon: "check-circle-outline",
							description: "Provide rationale for your decisions.",
							maxScore: 5,
						},
					],
				},
				metadata: {
					type: "INTEGRATED_WRITING_RESPONSE",
					skill: "WRITING",
					title: "Role-Play Writing Response",
					topics: ["Stakeholder Communication"],
				},
				score: null,
			},
		],
	},
	// Set 3 – Marking (Annotate Item 31)
	{
		type: "SET",
		stimulus: {
			id: 50120,
			content: {
				instruction:
					"Watch the video and then complete the annotation task below.",
				passage: {
					title: "ItemForge Output Review – Item 31 Annotation",
					paragraphs: [
						{
							text: "Elevate Learning's AI item generation system, ItemForge, produced a Grade 8 life science item whose question and answer key are correct, but whose student-facing rationale contains a fabricated academic citation. Maya Osei, VP of Product, has flagged this item for review. Watch her video for context, then examine the item output shown in the image and complete the annotation task.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "video3",
						url: "vi2oyGWjSM7VyJ81UQW00tu1",
						type: "video",
						transcript: `Team, it's Maya. Urgent update on ItemForge. We have a major problem with the Grade 8 science batch that went live Friday. We’ve received dozens of tickets and district-level complaints already. Item 17 has a wrong key—meiosis versus mitosis. Item 31 includes a hallucinated citation to a non-existent study. And Item 9 is flagging scientifically valid answers as wrong. This has hit twelve thousand students.

I also need a draft explanation for our school partners that is honest and clear about these AI hallucinations. We need to be transparent, not defensive, with our teachers and administrators. Production is halted immediately. I need a root cause audit and a plan for mandatory human guardrails by EOD. The logs are in the shared drive. Let’s get on this.`,
					},
				],
			},
			metadata: {
				type: "INTEGRATED_MARKING_ANSWER_STIMULUS",
				skill: "MARKING",
				title: "Integrated Marking Answer Stimulus",
				topics: ["AI Hallucination"],
			},
		},
		items: [
			{
				id: 50121,
				versionId: 1,
				content: {
					parentId: 50120,
					parentType: "SET",
					stem: "Review the ItemForge output for Item 31 shown in the image below. Circle the exact section where the AI hallucination occurs.",
					choices: [],
					instruction: "Circle and annotate the image as instructed.",
					media: [
						{
							id: "https://services.memorang.com/storage/v1/object/public/memorang-assets/demo/acme/incorrect_item.png",
							url: "https://services.memorang.com/storage/v1/object/public/memorang-assets/partners/acme/rocket-test-prep/ai_assessment_small.jpeg",
							type: "image",
						},
					],
				},
				tags: [
					{
						id: 6491,
						type: "Skill",
						label: "Marking",
						breadcrumb: "Marking",
					},
				],
				response: {
					maximumScore: 10,
					subskillsSchema: [
						{
							name: "Accuracy",
							icon: "crosshairs-gps",
							description: "Precisely identify the abnormal spike.",
							maxScore: 5,
						},
						{
							name: "Speed",
							icon: "timer-outline",
							description: "Identify the error area quickly.",
							maxScore: 5,
						},
					],
				},
				metadata: {
					type: "MARKING_ANSWER",
					skill: "MARKING",
					title: "Item 31 Hallucination Annotation",
					topics: ["AI Hallucination"],
				},
				score: null,
			},
		],
	},
	// Set 4 – Speaking (Verbal Explanation to Internal Team)
	{
		type: "SET",
		stimulus: {
			id: 50130,
			content: {
				instruction: "Watch the video and record your answer.",
				passage: {
					title:
						"Speaking Scenario: Explaining the Incident to the Product Team",
					paragraphs: [
						{
							text: "After investigating the ItemForge incident, you have been asked by Maya Osei to verbally explain to the broader product team what AI hallucination is, why it occurred in this specific case, and which category of technical fix is most appropriate. Watch her video for context, then record your response.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "video4",
						url: "vi2oyGWjSM7VyJ81UQW00tu1",
						type: "video",
						transcript: `Team, it's Maya. Urgent update on ItemForge. We have a major problem with the Grade 8 science batch that went live Friday. We’ve received dozens of tickets and district-level complaints already. Item 17 has a wrong key—meiosis versus mitosis. Item 31 includes a hallucinated citation to a non-existent study. And Item 9 is flagging scientifically valid answers as wrong. This has hit twelve thousand students.

I also need a draft explanation for our school partners that is honest and clear about these AI hallucinations. We need to be transparent, not defensive, with our teachers and administrators. Production is halted immediately. I need a root cause audit and a plan for mandatory human guardrails by EOD. The logs are in the shared drive. Let’s get on this.`,
					},
				],
			},
			metadata: {
				type: "LS_STIMULUS",
				skill: "SPEAKING",
				title: "Listen Speak Stimulus",
				topics: ["AI Literacy"],
			},
		},
		items: [
			{
				id: 50131,
				type: "AUDIO_INPUT_EMOTION",
				content: {
					parentId: 50130,
					parentType: "SET",
					stem: "Record an audio response in which you explain to Maya and the product team:\n- what AI hallucination is in plain terms\n- why ItemForge produced hallucinated content in this specific case\n- what misconceptions about AI the team should be aware of\n- which category of fix — evaluator, guardrail, retrieval grounding, or output validation — best addresses each failure mode identified in the incident.",
					choices: [],
					instruction: "Record your answer.",
					media: null,
				},
				tags: [
					{
						id: 6492,
						type: "Skill",
						label: "Speaking",
						breadcrumb: "Speaking",
					},
				],
				response: {
					maximumScore: 30,
					maximumDuration: 40,
					subskillsSchema: [
						{
							maxScore: 5,
							name: "Communication & Interpersonal Skills",
							icon: "message-outline",
							description: "Demonstrate effective verbal communication.",
						},
						{
							maxScore: 5,
							name: "Responsibility",
							icon: "account-check-outline",
							description: "Show accountability for the incident.",
						},
						{
							maxScore: 5,
							name: "Clarity",
							icon: "microphone-outline",
							description: "Speak clearly and coherently.",
						},
						{
							maxScore: 5,
							name: "Professionalism",
							icon: "briefcase-outline",
							description: "Maintain a professional tone.",
						},
						{
							maxScore: 5,
							name: "Solution Orientation",
							icon: "arrow-right-circle-outline",
							description: "Present clear corrective actions.",
						},
						{
							maxScore: 5,
							name: "Empathy",
							icon: "heart-outline",
							description:
								"Convey understanding and empathy toward stakeholders.",
						},
					],
				},
				metadata: {
					type: "AUDIO_INPUT_EMOTION",
					skill: "SPEAKING",
					title: "Audio Response Item",
					topics: ["AI Literacy"],
				},
				score: null,
			},
		],
	},
	// Set 5 – Writing (Post-Incident Report)
	{
		type: "SET",
		stimulus: {
			id: 50140,
			content: {
				instruction: "Watch the video and then respond as instructed.",
				passage: {
					title: "ItemForge Incident – Post-Incident Report",
					paragraphs: [
						{
							text: "Elevate Learning's AI item generation system, ItemForge, produced a batch of Grade 8 life science assessment items containing three distinct failure modes: a wrong answer key (Item 17), a fabricated academic citation in the student-facing rationale (Item 31), and an ambiguous item with two valid answers (Item 9). The batch was served to over 12,000 students across 200 schools before the errors were detected. Maya Osei, VP of Product, has requested a formal post-incident report. Watch her video below for full context, then complete the report.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "video5",
						url: "vi2oyGWjSM7VyJ81UQW00tu1",
						type: "video",
						transcript: `Team, it's Maya. Urgent update on ItemForge. We have a major problem with the Grade 8 science batch that went live Friday. We’ve received dozens of tickets and district-level complaints already. Item 17 has a wrong key—meiosis versus mitosis. Item 31 includes a hallucinated citation to a non-existent study. And Item 9 is flagging scientifically valid answers as wrong. This has hit twelve thousand students.

I also need a draft explanation for our school partners that is honest and clear about these AI hallucinations. We need to be transparent, not defensive, with our teachers and administrators. Production is halted immediately. I need a root cause audit and a plan for mandatory human guardrails by EOD. The logs are in the shared drive. Let’s get on this.`,
					},
				],
			},
			metadata: {
				type: "INTEGRATED_WRITING_STIMULUS",
				skill: "WRITING",
				title: "Post-Incident Report Writing Stimulus",
				topics: ["AI Governance"],
			},
		},
		items: [
			{
				id: 50141,
				versionId: 1,
				content: {
					parentId: 50140,
					parentType: "SET",
					stem: "Based on Maya's video and the three failure modes identified in the ItemForge incident, compose a comprehensive post-incident report that includes:\n- a timeline of events from item generation to incident detection\n- a root cause analysis for each failure mode — wrong answer key, fabricated citation, and construct validity failure\n- an assessment of the impact on students and school partners\n- the guardrails and evaluators you recommend implementing before ItemForge resumes operation\n- a proposed validation process for future AI-generated item batches\nJustify each recommendation.",
					choices: [],
					instruction: "Write your response in the text box provided.",
					media: null,
				},
				tags: [
					{
						id: 6491,
						type: "Skill",
						label: "Writing",
						breadcrumb: "Writing",
					},
				],
				response: {
					maximumScore: 20,
					subskillsSchema: [
						{
							name: "Organization",
							icon: "file-tree-outline",
							description: "Organize your report logically.",
							maxScore: 5,
						},
						{
							name: "Accountability",
							icon: "check-circle-outline",
							description: "Demonstrate responsibility and transparency.",
							maxScore: 5,
						},
						{
							name: "Actionable Recommendations",
							icon: "lightbulb-outline",
							description: "Offer practical measures for prevention.",
							maxScore: 5,
						},
						{
							name: "Detail Orientation",
							icon: "file-document-outline",
							description: "Include all relevant details in your report.",
							maxScore: 5,
						},
					],
				},
				metadata: {
					type: "INTEGRATED_WRITING_RESPONSE",
					skill: "WRITING",
					title: "Post-Incident Report Response",
					topics: ["AI Governance"],
					taskType: "default",
				},
				score: null,
			},
		],
	},
];
